<script setup lang="ts">
// Games list used by the scoreboard rail/Games tab and the Team page. Rows link
// to the Game page. Pass live/upcoming/final sections, or a single `flat` list
// (used for the fixtures fallback) where each row shows its own status.
interface Game {
  event_id: string; sport: string; group?: string
  home_team: string; away_team: string; commence_time: string | null
  status: string; home_score: number | null; away_score: number | null
  final: boolean; result?: string
}
defineProps<{
  live?: Game[]; upcoming?: Game[]; final?: Game[]; flat?: Game[]
  showMeta?: boolean
}>()
const { fmtDateTime } = useClientFormat()
function scoreLine(g: Game) {
  if (g.home_score == null || g.away_score == null) return ''
  return `${g.away_score} — ${g.home_score}`
}
</script>

<template>
  <div class="games">
    <template v-if="flat">
      <NuxtLink v-for="g in flat" :key="g.event_id" class="g-row"
        :class="{ live: g.status === 'live' }" :to="`/scoreboard/game/${g.event_id}`">
        <div class="g-main">
          <div class="g-teams">{{ g.away_team }} <span class="at">@</span> {{ g.home_team }}</div>
          <div class="g-meta mono">{{ fmtDateTime(g.commence_time) }}</div>
        </div>
        <div class="g-side">
          <div v-if="scoreLine(g)" class="g-score mono">{{ scoreLine(g) }}</div>
          <div class="g-st" :class="g.final ? 'final' : g.status">{{ g.final ? 'final' : g.status }}</div>
        </div>
      </NuxtLink>
      <p v-if="!flat.length" class="empty">No games to show.</p>
    </template>

    <template v-else>
      <section v-if="live && live.length">
        <h3 class="sec"><span class="dot live"></span> live</h3>
        <NuxtLink v-for="g in live" :key="g.event_id" class="g-row live" :to="`/scoreboard/game/${g.event_id}`">
          <div class="g-main">
            <div class="g-teams">{{ g.away_team }} <span class="at">@</span> {{ g.home_team }}</div>
            <div v-if="showMeta" class="g-meta mono">{{ g.sport }}</div>
          </div>
          <div class="g-side"><div class="g-score mono">{{ scoreLine(g) || 'in progress' }}</div></div>
        </NuxtLink>
      </section>

      <section v-if="upcoming && upcoming.length">
        <h3 class="sec">upcoming</h3>
        <NuxtLink v-for="g in upcoming" :key="g.event_id" class="g-row" :to="`/scoreboard/game/${g.event_id}`">
          <div class="g-main">
            <div class="g-teams">{{ g.away_team }} <span class="at">@</span> {{ g.home_team }}</div>
            <div class="g-meta mono">{{ fmtDateTime(g.commence_time) }}</div>
          </div>
          <div class="g-side"><div class="g-st scheduled">scheduled</div></div>
        </NuxtLink>
      </section>

      <section v-if="final && final.length">
        <h3 class="sec">final</h3>
        <NuxtLink v-for="g in final" :key="g.event_id" class="g-row" :to="`/scoreboard/game/${g.event_id}`">
          <div class="g-main">
            <div class="g-teams">{{ g.away_team }} <span class="at">@</span> {{ g.home_team }}</div>
            <div class="g-meta mono">{{ fmtDateTime(g.commence_time) }}</div>
          </div>
          <div class="g-side">
            <div class="g-score mono">{{ scoreLine(g) }}</div>
            <div class="g-st" :class="g.final ? 'final' : g.status">{{ g.final ? 'final' : g.status }}</div>
          </div>
        </NuxtLink>
      </section>

      <p v-if="!(live && live.length) && !(upcoming && upcoming.length) && !(final && final.length)"
        class="empty">No games on the board right now.</p>
    </template>
  </div>
</template>

<style scoped>
.sec { font-size: 12px; text-transform: uppercase; letter-spacing: 0.12em; color: var(--muted);
  margin: 18px 0 9px; display: flex; align-items: center; gap: 8px; }
.sec:first-child { margin-top: 0; }
.dot { width: 8px; height: 8px; border-radius: 50%; display: inline-block; }
.dot.live { background: var(--warn); animation: pulse 1.6s infinite; }
@keyframes pulse { 0% { box-shadow: 0 0 0 0 rgba(255,93,114,0.5); } 70% { box-shadow: 0 0 0 7px rgba(255,93,114,0); } 100% { box-shadow: 0 0 0 0 rgba(255,93,114,0); } }
.g-row { display: flex; justify-content: space-between; gap: 14px; padding: 12px 14px;
  border: 1px solid var(--line); border-radius: 11px; margin-bottom: 8px; background: var(--panel); }
.g-row:hover { border-color: var(--accent); }
.g-row.live { border-color: var(--warn); }
.g-teams { font-weight: 700; }
.g-teams .at { color: var(--muted); font-weight: 400; margin: 0 4px; }
.g-meta { font-size: 11px; color: var(--muted); margin-top: 3px; }
.g-side { text-align: right; min-width: 84px; }
.g-score { font-size: 17px; font-weight: 700; color: var(--ink); }
.g-st { font-family: var(--font-mono); font-size: 10px; text-transform: uppercase; color: var(--muted); margin-top: 3px; }
.g-st.final { color: var(--accent); }
.g-st.live { color: var(--warn); }
.g-st.postponed, .g-st.cancelled { color: var(--warn); opacity: 0.85; }
.empty { color: var(--muted); text-align: center; padding: 28px 16px; }
</style>
